import axios from 'axios';
import {
  documentDirectory,
  getInfoAsync,
  readAsStringAsync,
  writeAsStringAsync,
  EncodingType,
} from 'expo-file-system/legacy';
import { osName, osVersion } from 'expo-device';
import { Platform } from 'react-native';
import moment from 'moment';
import constants from '@/config/constants';
import { version } from '../../package.json';

const api = axios.create({
  baseURL: constants.address,
  timeout: 10000,
});

const LOG_FILE = `${documentDirectory}network_log.log`;

const ALLOWED_HEADERS = [
  'Host',
  'User-Agent',
  'Accept',
  'Accept-Language',
  'Accept-Encoding',
  'Referer',
  'Connection',
  'Content-Type',
  'Content-Length',
  'Date',
  'X-Forwarded-For',
  'Via',
  'X-Real-Ip',
  'X-Forwarded-Proto',
  'X-Forwarded-Host',
  'X-Forwarded-Port',
  'X-App-Id',
  'X-App-Version',
  'X-App-Platform',
  'X-App-Platform-Version',
  'X-Api-Version',
];

const filterHeaders = (headers: Record<string, string>) => {
  return Object.keys(headers).reduce(
    (filtered, key) => {
      if (ALLOWED_HEADERS.some((h) => h.toLowerCase() === key.toLowerCase())) {
        filtered[key] = headers[key];
      }
      return filtered;
    },
    {} as Record<string, string>,
  );
};

const getAppHeaders = (): Record<string, string> => ({
  'X-App-Id': `neolexon-aphasia-${constants.appMode}`,
  'X-App-Version': version,
  'X-App-Platform': Platform.OS,
  'X-App-Platform-Version': `osName: ${osName}; osVersion: ${osVersion}`,
  'X-Api-Version': constants.apiVersion,
});

// Function to write logs, overwriting each day
const logRequest = async (logEntry: string) => {
  if (Platform.OS !== 'web') {
    try {
      const now = moment();
      const todayDate = now.format('YYYY-MM-DD');

      // Read the existing log to check if it’s from today
      const fileExists = await getInfoAsync(LOG_FILE);
      let currentLog = '';

      if (fileExists.exists) {
        currentLog = await readAsStringAsync(LOG_FILE);
        const firstLine = currentLog.split('\n')[0];

        // If the first log entry isn't from today, reset the file
        if (!currentLog || !firstLine.includes(todayDate)) {
          currentLog = ''; // Reset log file for new day
        }
      }

      // Add new log entry
      const updatedLog = `${currentLog}${logEntry}`;
      await writeAsStringAsync(LOG_FILE, updatedLog, {
        encoding: EncodingType.UTF8,
      });
    } catch (error) {
      // failed to write log
      return Promise.reject(error);
    }
  }
};

// Request interceptor
api.interceptors.request.use((request) => {
  const appHeaders = getAppHeaders();

  Object.entries(appHeaders).forEach(([key, value]) => {
    request.headers?.set(key, value);
  });

  const logEntry = `[${moment().format('YYYY-MM-DD HH:mm:ss')}] Request: ${request.method?.toUpperCase()} ${request.baseURL}${request.url} - Headers: ${JSON.stringify(filterHeaders(request.headers || {}))}\n`;
  logRequest(logEntry);
  return request;
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    response.headers = {
      ...response.headers,
      ...getAppHeaders(),
    };
    const logEntry = `[${moment().format('YYYY-MM-DD HH:mm:ss')}] Response: ${response.status} ${response.config.url} - Headers: ${JSON.stringify(filterHeaders(response.headers || {}))}\n`;
    logRequest(logEntry);
    return response;
  },
  (error) => {
    const logEntry = `[${moment().format('YYYY-MM-DD HH:mm:ss')}] Error: ${error.response?.status || 'Unknown'} ${error.config?.url} - Message: ${error.message}\n`;
    logRequest(logEntry);
    return Promise.reject(error);
  },
);

export default api;
